<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Manage Subject Titles</h1>
        <Button label="Add Subject Title" icon="pi pi-plus" @click="openAdd" />
      </div>

      <DataTable :value="items" :paginator="true" :rows="10" :loading="loading" stripedRows showGridlines v-model:filters="filters" filterDisplay="menu">
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="name" header="Subject Title" sortable />
        <Column header="Actions" style="width: 10rem">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" @click="openEdit(data)" />
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteItem(data.id)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Subject Title' : 'Add Subject Title'" :modal="true" :style="{width: '400px'}">
      <div class="form-group" style="padding: 1rem 0;">
        <FloatLabel variant="in">
          <InputText id="name" v-model="form.name" class="w-full" />
          <label for="name">Subject Title *</label>
        </FloatLabel>
      </div>
      <div class="modal-actions">
        <Button label="Cancel" severity="secondary" icon="pi pi-times" @click="showModal = false" />
        <Button :label="isEditing ? 'Update' : 'Create'" icon="pi pi-check" :loading="saving" @click="save" />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const items = ref([])
const loading = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const form = ref({ id: null, name: '' })
const filters = ref({ global: { value: null, matchMode: 'contains' } })

const load = async () => {
  loading.value = true
  try {
    const res = await api.get('/subject-titles')
    items.value = res.data
  } catch (e) { console.error(e) }
  finally { loading.value = false }
}

const openAdd = () => {
  isEditing.value = false
  form.value = { id: null, name: '' }
  showModal.value = true
}

const openEdit = (item) => {
  isEditing.value = true
  form.value = { ...item }
  showModal.value = true
}

const save = async () => {
  if (!form.value.name) return
  saving.value = true
  try {
    if (isEditing.value) {
      await api.put(`/subject-titles/${form.value.id}`, { name: form.value.name })
    } else {
      await api.post('/subject-titles', { name: form.value.name })
    }
    showModal.value = false
    load()
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally { saving.value = false }
}

const deleteItem = async (id) => {
  const result = await Swal.fire({ title: 'Delete?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/subject-titles/${id}`)
      load()
    } catch (e) { Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' }) }
  }
}

onMounted(load)
</script>

<style scoped>
.page { padding: 1rem 0; background: var(--p-surface-50); min-height: 100vh; }
.container { margin: 1rem; padding: 1.5rem; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid #1e3a8a; }
.page-header h1 { color: #1e3a8a; font-size: 2rem; font-weight: 700; }
.table-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.actions { display: flex; gap: 0.5rem; }
.form-group .p-inputtext { width: 100% !important; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; padding-top: 1rem; border-top: 1px solid #e5e7eb; }
</style>
